import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType } from 'lightweight-charts';
import { Maximize2, Minimize2 } from 'lucide-react';

/**
 * BacktestingChart - Visualizes OHLC data, indicators, and trade signals
 */
const BacktestingChart = ({ data, trades, strategyParams, height = 450 }) => {
  const chartContainerRef = useRef();
  const wrapperRef = useRef();
  const chartRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
        wrapperRef.current.requestFullscreen().catch(err => {
            console.error(`Error attempting to enable full-screen mode: ${err.message}`);
        });
    } else {
        document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
        setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const chartOptions = {
        layout: {
            textColor: '#444',
            background: { type: ColorType.Solid, color: '#ffffff' },
            fontFamily: "'Inter', sans-serif",
        },
        grid: {
            vertLines: { color: '#f0f3fa' },
            horzLines: { color: '#f0f3fa' },
        },
        rightPriceScale: {
            borderColor: '#dfe2e9',
        },
        timeScale: {
            borderColor: '#dfe2e9',
            timeVisible: true,
            secondsVisible: false,
        },
        crosshair: {
            mode: 0,
            vertLine: { color: '#2962FF', labelBackgroundColor: '#2962FF' },
            horzLine: { color: '#2962FF', labelBackgroundColor: '#2962FF' },
        },
    };

    const handleResize = () => {
        if (chartRef.current && chartContainerRef.current) {
            chartRef.current.applyOptions({ 
                width: chartContainerRef.current.clientWidth,
                height: isFullScreen ? window.innerHeight - 100 : height
            });
        }
    };

    const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: isFullScreen ? window.innerHeight - 100 : height,
        ...chartOptions,
    });
    chartRef.current = chart;

    // Add Candlestick Series
    const gemstoneCandleSeries = chart.addCandlestickSeries({
        upColor: '#089981',
        downColor: '#f23645',
        borderVisible: false,
        wickUpColor: '#089981',
        wickDownColor: '#f23645',
    });

    // Format data for lightweight-charts
    const formattedData = data.map(item => ({
        time: Math.floor(new Date(item.timestamp).getTime() / 1000),
        open: parseFloat(item.open),
        high: parseFloat(item.high),
        low: parseFloat(item.low),
        close: parseFloat(item.close),
    })).sort((a, b) => a.time - b.time);

    gemstoneCandleSeries.setData(formattedData);

    // Calculate indicators
    const calculateEMA = (ohlc, period) => {
        const k = 2 / (period + 1);
        let emaData = [];
        let prevEma = parseFloat(ohlc[0].close);
        
        for (let i = 0; i < ohlc.length; i++) {
            const currentClose = parseFloat(ohlc[i].close);
            const currentEma = i === 0 ? currentClose : (currentClose * k) + (prevEma * (1 - k));
            emaData.push({
                time: Math.floor(new Date(ohlc[i].timestamp).getTime() / 1000),
                value: currentEma
            });
            prevEma = currentEma;
        }
        return emaData;
    };

    if (strategyParams && strategyParams.fastEMA) {
        const fastEmaSeries = chart.addLineSeries({
            color: '#2962FF',
            lineWidth: 2,
            title: `Fast EMA (${strategyParams.fastEMA})`,
        });
        fastEmaSeries.setData(calculateEMA(data, strategyParams.fastEMA));
    }

    if (strategyParams && strategyParams.slowEMA) {
        const slowEmaSeries = chart.addLineSeries({
            color: '#FF9800',
            lineWidth: 2,
            title: `Slow EMA (${strategyParams.slowEMA})`,
        });
        slowEmaSeries.setData(calculateEMA(data, strategyParams.slowEMA));
    }

    // Add Markers for Trades
    if (trades && trades.length > 0) {
        const markers = [];
        trades.forEach(trade => {
            const tradeTime = Math.floor(new Date(trade.timestamp).getTime() / 1000);
            
            if (trade.type === 'OPEN_LONG') {
                markers.push({
                    time: tradeTime,
                    position: 'belowBar',
                    color: '#2196F3', // Blue
                    shape: 'arrowUp',
                    text: 'BUY @ ' + trade.price.toFixed(2),
                });
            } else if (trade.type === 'OPEN_SHORT') {
                markers.push({
                    time: tradeTime,
                    position: 'aboveBar',
                    color: '#e91e63', // Pink
                    shape: 'arrowDown',
                    text: 'SELL @ ' + trade.price.toFixed(2),
                });
            } else if (trade.type.startsWith('CLOSE')) {
                markers.push({
                    time: tradeTime,
                    position: trade.type === 'CLOSE_LONG' ? 'aboveBar' : 'belowBar',
                    color: trade.profit >= 0 ? '#4caf50' : '#757575',
                    shape: 'circle',
                    text: 'EXIT: ' + (trade.profit >= 0 ? '+' : '') + trade.profit.toFixed(2),
                });
            }
        });

        markers.sort((a, b) => a.time - b.time);
        gemstoneCandleSeries.setMarkers(markers);
    }

    chart.timeScale().fitContent();
    window.addEventListener('resize', handleResize);

    return () => {
        window.removeEventListener('resize', handleResize);
        chart.remove();
    };
  }, [data, trades, strategyParams, height, isFullScreen]);

  return (
    <div 
        ref={wrapperRef}
        className={`chart-wrapper p-3 border rounded bg-white shadow-sm ${isFullScreen ? 'fullscreen-mode' : ''}`}
        style={{ position: 'relative' }}
    >
        <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-3">
                <small className="d-flex align-items-center"><span className="badge bg-primary rounded-circle p-1 me-1" style={{width: 8, height: 8}}> </span> Fast EMA</small>
                <small className="d-flex align-items-center"><span className="badge rounded-circle p-1 me-1" style={{width: 8, height: 8, backgroundColor: '#FF9800'}}> </span> Slow EMA</small>
            </div>
            <button 
                onClick={toggleFullScreen}
                className="btn btn-sm btn-outline-secondary rounded-circle p-2 d-flex align-items-center justify-content-center shadow-none"
                style={{ width: 32, height: 32 }}
                title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
                {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
        </div>

        <div ref={chartContainerRef} style={{ width: '100%' }} />
        
        <div className="chart-info-overlay mt-2">
            <span className="xx-small text-muted d-flex align-items-center">
                <i className="fas fa-mouse me-2"></i> Use scroll to zoom, Drag to move, Markers show Signal Execution
            </span>
        </div>

        {isFullScreen && (
            <style>{`
                .fullscreen-mode {
                    width: 100vw !important;
                    height: 100vh !important;
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    z-index: 9999 !important;
                    padding: 20px !important;
                    border: none !important;
                    border-radius: 0 !important;
                }
            `}</style>
        )}
    </div>
  );
};

export default BacktestingChart;
